/**
 * Analytics Configuration for PinDay
 * GA4 and Meta Pixel event tracking utilities
 */

// GA4 Measurement ID - Replace with your actual ID
export const GA4_MEASUREMENT_ID = 'G-XZ8WE2HWB1'
export const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || '1633307431033125'

// Event types for SEO/conversion tracking
export type ConversionEvent = 
  | 'sign_up'
  | 'login'
  | 'first_proposal_created'
  | 'proposal_sent'
  | 'payment_received'
  | 'subscription_started'
  | 'subscription_upgraded'

export type TrafficSource = 'organic' | 'direct' | 'referral' | 'paid' | 'social'

interface EventParams {
  [key: string]: string | number | boolean | undefined
}

type MetaPixelFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[][]
  push?: (...args: unknown[]) => void
  loaded?: boolean
  version?: string
}

/**
 * Initialize GA4 (called once on app load)
 */
export function initGA4() {
  if (typeof window === 'undefined') return
  
  // Skip if already initialized or no measurement ID
  if ('gtag' in window || !GA4_MEASUREMENT_ID) return
  
  // Load gtag.js script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`
  document.head.appendChild(script)
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(args)
  }
  
  window.gtag('js', new Date())
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: false,
  })
}

/**
 * Initialize Meta Pixel (called once on app load)
 */
export function initMetaPixel() {
  if (typeof window === 'undefined' || !META_PIXEL_ID) return

  if (window.__metaPixelInitialized) return

  if (window.fbq?.loaded) {
    window.fbq('init', META_PIXEL_ID)
    window.__metaPixelInitialized = true
    return
  }

  const fbq: MetaPixelFn = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args)
      return
    }

    fbq.queue = fbq.queue || []
    fbq.queue.push(args)
  } as MetaPixelFn

  fbq.queue = fbq.queue || []
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.push = fbq

  window.fbq = fbq
  window._fbq = fbq

  const existingScript = document.querySelector<HTMLScriptElement>(
    'script[src="https://connect.facebook.net/en_US/fbevents.js"]',
  )

  if (!existingScript) {
    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)
  }

  window.fbq('init', META_PIXEL_ID)
  window.__metaPixelInitialized = true
}

/**
 * Track page view (for SPA navigation)
 */
export function trackPageView(path: string, title?: string) {
  if (typeof window === 'undefined') return

  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    })
  }

  if (window.fbq) {
    window.fbq('track', 'PageView')
  }
}

/**
 * Track conversion events
 */
export function trackConversion(event: ConversionEvent, params?: EventParams) {
  if (typeof window === 'undefined' || !window.gtag) return
  
  const trafficSource = getTrafficSource()
  
  window.gtag('event', event, {
    ...params,
    traffic_source: trafficSource,
    timestamp: new Date().toISOString(),
  })
  
  // Log for debugging in development
  if (import.meta.env.DEV) {
    console.log(`[Analytics] ${event}`, { ...params, traffic_source: trafficSource })
  }
}

/**
 * Track SEO-specific conversion (user came from organic search)
 */
export function trackSEOConversion(event: ConversionEvent, params?: EventParams) {
  const trafficSource = getTrafficSource()
  
  if (trafficSource === 'organic') {
    trackConversion(event, {
      ...params,
      seo_conversion: true,
    })
  } else {
    trackConversion(event, params)
  }
}

/**
 * Get traffic source from referrer/UTM params
 */
export function getTrafficSource(): TrafficSource {
  if (typeof window === 'undefined') return 'direct'
  
  const urlParams = new URLSearchParams(window.location.search)
  const utmSource = urlParams.get('utm_source')
  const utmMedium = urlParams.get('utm_medium')
  const referrer = document.referrer
  
  // Check UTM params first
  if (utmMedium === 'cpc' || utmMedium === 'paid') return 'paid'
  if (utmSource === 'google' || utmSource === 'bing') {
    return utmMedium === 'organic' ? 'organic' : 'paid'
  }
  if (utmMedium === 'social' || ['facebook', 'instagram', 'twitter', 'linkedin'].includes(utmSource || '')) {
    return 'social'
  }
  
  // Check referrer
  if (!referrer) return 'direct'
  
  const referrerDomain = new URL(referrer).hostname
  
  // Search engines
  if (['google.com', 'google.com.br', 'bing.com', 'yahoo.com', 'duckduckgo.com'].some(d => referrerDomain.includes(d))) {
    return 'organic'
  }
  
  // Social networks
  if (['facebook.com', 'instagram.com', 'twitter.com', 'linkedin.com', 't.co'].some(d => referrerDomain.includes(d))) {
    return 'social'
  }
  
  // Same domain = direct
  if (referrerDomain.includes('pinday.me')) return 'direct'
  
  return 'referral'
}

/**
 * Store traffic source in session for later attribution
 */
export function storeTrafficSource() {
  if (typeof window === 'undefined') return
  
  const source = getTrafficSource()
  const existing = sessionStorage.getItem('traffic_source')
  
  // Only store if not already set (first touch attribution)
  if (!existing) {
    sessionStorage.setItem('traffic_source', source)
    sessionStorage.setItem('landing_page', window.location.pathname)
  }
}

/**
 * Get stored traffic source
 */
export function getStoredTrafficSource(): TrafficSource {
  if (typeof window === 'undefined') return 'direct'
  return (sessionStorage.getItem('traffic_source') as TrafficSource) || getTrafficSource()
}

/**
 * Blog Analytics Events
 */
export type BlogAnalyticsEvent = 'blog_view' | 'blog_click' | 'newsletter_signup' | 'comment_posted'

export function trackBlogView(postSlug: string, language: string, postTitle?: string) {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('event', 'blog_view', {
    blog_slug: postSlug,
    blog_title: postTitle || postSlug,
    blog_language: language,
    page_path: `/${language}/blog/${postSlug}`,
    event_category: 'Blog',
    event_label: postTitle || postSlug,
  })
}

export function trackBlogLink(postSlug: string, language: string, linkUrl: string) {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('event', 'blog_click', {
    blog_slug: postSlug,
    blog_language: language,
    link_url: linkUrl,
    link_domain: new URL(linkUrl).hostname,
    event_category: 'Blog',
    event_label: linkUrl,
  })
}

export function trackNewsletterSignup(postSlug: string, language: string) {
  trackConversion('sign_up', {
    post_slug: postSlug,
    language: language,
    source: `blog-${postSlug}`,
  })
}

export function trackCommentPosted(postSlug: string, language: string) {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('event', 'comment_posted', {
    blog_slug: postSlug,
    blog_language: language,
    source: `blog-${postSlug}`,
    event_category: 'Blog',
    event_label: 'Comment Posted',
  })
}

/**
 * Engagement tracking: scroll depth
 */
export function trackScrollDepth(depth: number, pagePath: string, pageTitle: string) {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('event', 'scroll', {
    scroll_depth: depth,
    page_path: pagePath,
    page_title: pageTitle,
    event_category: 'Engagement',
    event_label: `${depth}%`,
  })
}

/**
 * Engagement tracking: time on page
 */
export function trackTimeOnPage(timeSeconds: number, pagePath: string, pageTitle: string) {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('event', 'time_on_page', {
    time_on_page: timeSeconds,
    page_path: pagePath,
    page_title: pageTitle,
    event_category: 'Engagement',
    value: timeSeconds,
  })
}

// Extend window type for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
    fbq?: MetaPixelFn
    _fbq?: MetaPixelFn
    __metaPixelInitialized?: boolean
  }
}
